import { getInitData } from './init-data';
import type { ApiError } from './types';

const BASE = import.meta.env.VITE_API_BASE ?? '/api/v1';

export class ApiCallError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(
  method: string,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Authorization', `tma ${getInitData()}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(`${BASE}${path}`, { ...init, method, headers });
  if (!res.ok) {
    let body: ApiError = { detail: res.statusText, code: 'http_error' };
    try {
      body = (await res.json()) as ApiError;
    } catch {
      // non-JSON error body — keep defaults
    }
    throw new ApiCallError(res.status, body.code, body.detail);
  }
  // 204 No Content → return undefined as T.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function multipartRequest<T>(path: string, formData: FormData): Promise<T> {
  const headers = new Headers();
  headers.set('Authorization', `tma ${getInitData()}`);
  // Don't set Content-Type — browser fills in multipart boundary.
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers,
    body: formData,
  });
  if (!res.ok) {
    let body: ApiError = { detail: res.statusText, code: 'http_error' };
    try {
      body = (await res.json()) as ApiError;
    } catch {
      /* keep defaults */
    }
    throw new ApiCallError(res.status, body.code, body.detail);
  }
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) =>
    request<T>('POST', path, { body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>('DELETE', path),
  postMultipart: <T>(path: string, fd: FormData) => multipartRequest<T>(path, fd),
};
